import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";

import Label from "../../../../shared/ui/form/Label";
import { useCenter, useCenterMembers, useCenterPayments, useCenterPaymentsEnabled } from "../../../center";
import CenterAdminShell from "../components/CenterAdminShell";
import CenterPaymentSettingsCard from "../components/CenterPaymentSettingsCard";

const STATUSES = ["", "draft", "pending", "paid", "cancelled"];

/**
 * How the centre charges, and what is owed.
 *
 * The settings come first: they decide where the money for the centre's
 * patients goes. This is also where Stripe returns after the centre links its
 * account (`?stripe=connected|error`).
 *
 * Money owed to the centre's professionals.
 *
 * Amount, status and dates. The concept line is not here: it is free text the
 * therapist writes, and it routinely describes the work.
 */
export default function CenterPaymentsPage() {
    const { t } = useTranslation();
    const { center, loading } = useCenter();
    const { members } = useCenterMembers(center?.id);
    const { loading: policyLoading, enabled: paymentsEnabled } = useCenterPaymentsEnabled(
        center?.id
    );
    const [status, setStatus] = useState("");
    const {
        payments,
        total,
        loading: paymentsLoading,
    } = useCenterPayments(center?.id, {
        payment_status: status || undefined,
    });

    const therapistName = (id: string) => {
        const member = members.find((m) => m.therapist_id === id);
        return member ? `${member.first_name} ${member.last_name}` : "—";
    };

    if (loading || !center || policyLoading) {
        return <p className="p-6 text-sm text-gray-500">{t("common.loading")}</p>;
    }

    // With payments switched off the tab is gone; an old link lands on the overview.
    if (!paymentsEnabled) {
        return <Navigate to="/center" replace />;
    }

    return (
        <CenterAdminShell center={center}>
            <CenterPaymentSettingsCard centerId={center.id} />
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <h2 className="mb-1 text-lg font-semibold text-content-primary">
                    {t("center.payments.title")}
                </h2>
                <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                    {t("center.payments.subtitle")}
                </p>

                <div className="mb-5 w-56">
                    <Label htmlFor="payment-status">{t("center.payments.status")}</Label>
                    <select
                        id="payment-status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700"
                    >
                        {STATUSES.map((option) => (
                            <option key={option || "all"} value={option}>
                                {option
                                    ? t(`center.payments.states.${option}`)
                                    : t("center.payments.allStates")}
                            </option>
                        ))}
                    </select>
                </div>

                {paymentsLoading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

                {!paymentsLoading && payments.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("center.payments.empty")}
                    </p>
                )}

                {!paymentsLoading && payments.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                    <th className="py-2 pr-4 font-medium">
                                        {t("center.payments.who")}
                                    </th>
                                    <th className="py-2 pr-4 font-medium">
                                        {t("center.payments.therapist")}
                                    </th>
                                    <th className="py-2 pr-4 font-medium">
                                        {t("center.payments.amount")}
                                    </th>
                                    <th className="py-2 pr-4 font-medium">
                                        {t("center.payments.status")}
                                    </th>
                                    <th className="py-2 font-medium">
                                        {t("center.payments.dueDate")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((payment) => (
                                    <tr
                                        key={payment.payment_id}
                                        className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                    >
                                        <td className="py-3 pr-4 text-content-primary">
                                            {payment.patient_name}
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                            {therapistName(payment.therapist_id)}
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                            {payment.total}
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                            {t(`center.payments.states.${payment.status}`, {
                                                defaultValue: payment.status,
                                            })}
                                        </td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">
                                            {payment.due_date
                                                ? new Date(payment.due_date).toLocaleDateString()
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <p className="mt-4 text-xs text-content-tertiary">
                    {t("center.payments.total", { count: total })}
                </p>
            </section>
        </CenterAdminShell>
    );
}
